/* Финучет одной лентой: сводка за период, расходы по категориям,
   выгрузка ОДДС и P&L в Telegram, журнал операций. ТЗ 6.2, 7.1, 7.5, 7.6, 8. */

import { canEdit, canRead, ddmm, isTransfer, pct, rub, sectionLevel } from "@mera/shared";
import React, { useMemo, useState } from "react";
import { useNavigate } from "../lib/router.jsx";

import { List } from "konsta/react";

import AddButton from "../components/AddButton.jsx";
import { Avatar } from "../components/AvatarPicker.jsx";
import { FilterPeriod, FilterSelect } from "../components/FilterPill.jsx";
import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelFinance } from "../components/skeleton.jsx";
import {
  Bar, Empty, ErrorState, Eyebrow, Metric, MetricGrid, Money, Sep,
} from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch, useSticky } from "../lib/store.jsx";
import { alertDialog, haptic } from "../lib/tg.js";

/** Первое и последнее число месяца, в котором лежит дата */
function monthRange(iso) {
  const [y, m] = String(iso || "").split("-").map(Number);
  if (!y || !m) return { from: "", to: "" };
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, "0")}` };
}

export default function Finance() {
  const { me, today, meta } = useApp();
  const navigate = useNavigate();
  // «Только чтение» оставляет сводку, журнал и выгрузки, но прячет правки
  const finEdit = canEdit(sectionLevel(me, "finance"));
  /* Отбор запоминается на время сеанса: после захода в операцию и возврата
     (и после любого перехода по разделам) экран открывается с тем же бизнесом
     и периодом, что и был. */
  const [biz, setBiz] = useSticky("finance.biz", "all");
  // По умолчанию — текущий месяц целиком
  const [range, setRange] = useSticky("finance.range", () => monthRange(today));
  /* Категория режет только журнал: сводка сверху — картина всего периода,
     и урезать её до одной статьи значило бы показать выручку без выручки */
  const [cat, setCat] = useSticky("finance.cat", "all");
  /* Счёт, в отличие от категории, режет всё: и сводку, и журнал, и выгрузки —
     это вопрос «что происходит с деньгами на этом счёте» целиком */
  const [acc, setAcc] = useSticky("finance.acc", "all");

  const query = {
    biz,
    acc: acc === "all" ? undefined : acc,
    from: range.from || undefined,
    to: range.to || undefined,
  };

  /* Оба запроса ждём вместе: раньше каждый блок показывал свой прелоадер,
     и между ними успевали проявиться кнопки выгрузки. */
  const summary = useFetch("/finance/summary", query);
  const ops = useFetch("/operations", query);
  const loading = summary.loading || ops.loading;
  const failed = summary.error || ops.error;

  /* Фильтруем по бизнесу: контура как отдельной сущности больше нет, и деньги
     журнала делятся ровно по тому, чьи это счета */
  const businesses = useMemo(
    () => [{ id: "all", label: "Все бизнесы" }, ...(meta?.businesses || []).map((b) => ({ id: b.id, label: b.name }))],
    [meta]
  );

  /* Счета выбранного бизнеса; при «Все бизнесы» — все, с подписью бизнеса,
     иначе одинаковые «Наличные» разных бизнесов не различить */
  const accounts = useMemo(() => {
    const list = (meta?.businesses || [])
      .filter((b) => biz === "all" || b.id === biz)
      .flatMap((b) => (b.accounts || []).map((a) => ({
        id: a.id,
        label: biz === "all" ? `${a.name} · ${b.name}` : a.name,
      })));
    return [{ id: "all", label: "Все счета" }, ...list];
  }, [meta, biz]);

  /* В списке категорий — только те, что за период вообще встречаются: полный
     справочник наполовину состоял бы из пустых статей. Выбранную держим в списке
     всегда, иначе после смены периода пилюля осталась бы без подписи. */
  const categories = useMemo(() => {
    const seen = new Set((ops.data || []).map((o) => o.cat).filter(Boolean));
    const dict = [...(meta?.dict?.expenseCats || []), ...(meta?.dict?.incomeCats || [])];
    const list = dict.filter((c) => seen.has(c) || c === cat);
    return [{ id: "all", label: "Все категории" }, ...list.map((c) => ({ id: c, label: c }))];
  }, [ops.data, meta, cat]);

  // Журнал по выбранной категории; операции без категории попадают только в «Все»
  const journal = useMemo(
    () => mergeSplits(cat === "all" ? ops.data || [] : (ops.data || []).filter((o) => o.cat === cat)),
    [ops.data, cat]
  );

  if (!canRead(sectionLevel(me, "finance"))) {
    return <Screen logo><ErrorState text="Раздел закрыт администратором." /></Screen>;
  }

  return (
    <Screen logo>
      {/* Бизнес, счёт, период и категория — одной строкой; не влезло — прокручивается вбок */}
      <div className="relative hscroll flex items-center gap-2 px-4 pt-1 pb-2">
        <FilterSelect
          value={biz}
          onChange={(v) => {
            haptic.select();
            setBiz(v);
            // Счёт другого бизнеса в новом отборе не живёт — сбрасываем его
            if (v !== "all") {
              const own = meta?.businesses?.find((b) => b.id === v)?.accounts || [];
              if (!own.some((a) => a.id === acc)) setAcc("all");
            }
          }}
          options={businesses}
          ariaLabel="Бизнес"
        />
        <FilterSelect value={acc} onChange={(v) => { haptic.select(); setAcc(v); }}
          options={accounts} ariaLabel="Счёт" />
        <FilterPeriod
          from={range.from}
          to={range.to}
          thisYear={String(today || "").slice(0, 4)}
          onChange={setRange}
        />
        <FilterSelect value={cat} onChange={(v) => { haptic.select(); setCat(v); }}
          options={categories} ariaLabel="Категория" />
      </div>

      {loading ? (
        <SkelFinance />
      ) : failed ? (
        <ErrorState text={failed} onRetry={() => { summary.refetch(); ops.refetch(); }} />
      ) : (
        <>
          <Dash data={summary.data} />
          <Exports query={query} />
          <Journal
            data={journal}
            filtered={cat !== "all"}
            navigate={finEdit ? navigate : null}
          />
        </>
      )}

      {finEdit ? (
        <AddButton
          align="right"
          overTabbar
          label="Добавить операцию"
          onClick={() => { haptic.medium(); navigate("/edit/operations/new"); }}
        />
      ) : null}
    </Screen>
  );
}

/* ── Вкладка «Дашборд» (ТЗ 6.2.1) ── */
function Dash({ data }) {
  const maxCat = Math.max(...data.categories.map((c) => c.amount), 1);

  return (
    <>
      {/* Шесть показателей вместо четырёх, поэтому плитки плотные и по трое в ряд —
          иначе сводка занимала бы весь первый экран. Выручка делится на регулярную
          (подписки) и разовую: сумма этих двух и есть выручка. */}
      <MetricGrid cols={3} className="pt-1">
        <Metric label="Выручка" value={rub(data.revenue)} color="var(--fin-green)" />
        <Metric label="Расходы" value={rub(data.opex)} color="var(--fin-red)" />
        <Metric
          label="Прибыль"
          value={rub(data.profit)}
          color={data.profit >= 0 ? "var(--fin-green)" : "var(--fin-red)"}
        />
        <Metric label="MRR" value={rub(data.mrr)} color="var(--fin-blue)" />
        <Metric label="Разовые" value={rub(data.oneTime)} />
        <Metric label="Маржа" value={`${data.margin}%`} />
      </MetricGrid>

      <Eyebrow>Расходы по категориям</Eyebrow>
      <div className="px-4">
        <div className="card-tg py-1">
          {!data.categories.length ? (
            <Empty text="Расходов за период нет" />
          ) : (
            data.categories.map((c, i) => (
              <React.Fragment key={c.cat}>
                {i > 0 ? <Sep /> : null}
                <div className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="text-[14px] truncate">{c.cat}</span>
                    <span className="num text-[14px] font-semibold shrink-0">{rub(c.amount)}</span>
                  </div>
                  <Bar value={pct(c.amount, maxCat)} color="var(--fin-red)" />
                </div>
              </React.Fragment>
            ))
          )}
        </div>
      </div>

    </>
  );
}

/* Доли составного платежа приходят отдельными операциями — по одной на счёт.
   В журнале это одна строка: сумма — всех долей, что попали в отбор. Отобрали
   один бизнес или счёт — в строке только его доля, как и в его балансе. */
function mergeSplits(list) {
  const out = [];
  const byGroup = new Map();
  for (const o of list) {
    if (!o.groupId) { out.push(o); continue; }
    const row = byGroup.get(o.groupId);
    if (!row) {
      const head = { ...o, parts: [o] };
      byGroup.set(o.groupId, head);
      out.push(head);
    } else {
      row.parts.push(o);
      row.amount = Math.round((row.amount + o.amount) * 100) / 100;
    }
  }
  return out;
}

/* Подпись счетов составного платежа: разные бизнесы — их имена, один бизнес — сколько счетов */
function partsLabel(parts) {
  const bizs = [...new Set(parts.map((p) => p.biz).filter(Boolean))];
  return bizs.length > 1 ? bizs.join(" + ") : `${bizs[0] || ""} / ${parts.length} сч.`;
}

/* ── Вкладка «Журнал» (ТЗ 6.2.2) ──
   Строка открывает операцию по нажатию. Свайпа с кнопками больше нет: правка и
   удаление живут в самой карточке, а жест на каждой строке мешал прокрутке
   и прятал то, что и так есть на экране операции.
   Без navigate (только чтение) строки не нажимаются — журнал просто читается. */
function Journal({ data, filtered, navigate }) {
  // Пусто — просто говорим об этом: завести операцию есть чем, круглая кнопка рядом
  if (!data.length) {
    return <Empty text={filtered ? "Операций этой категории за период нет" : "Операций за период нет"} />;
  }

  return (
    <List strong inset>
      {data.map((o) => {
        const income = o.type === "Поступление";
        // Перевод не красим ни зелёным, ни красным: денег в группе не убавилось
        const transfer = isTransfer(o);
        return (
          <li
            key={o.id}
            className={`hairline-b flex items-center gap-3 px-4 py-2.5 ${navigate ? "active:opacity-60" : ""}`}
            role={navigate ? "button" : undefined}
            onClick={navigate ? () => { haptic.light(); navigate(`/edit/operations/${o.id}`); } : undefined}
          >
            <Avatar src={o.bizAvatar} name={o.biz} size={38} />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="num text-[12px] font-semibold" style={{ color: "var(--tg-hint)" }}>
                  {ddmm(o.date)}
                </span>
                <span className="text-[14px] font-medium truncate">
                  {o.note || (transfer ? "Перевод" : o.cat) || "Операция"}
                </span>
              </div>
              <div className="mt-0.5 text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                {[transfer ? "Перевод" : o.cat, o.projName, o.by, o.source === "telegram" ? "из бота" : null]
                  .filter(Boolean)
                  .join(" / ")}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <Money value={o.amount} sign={transfer ? null : income ? "in" : "out"} />
              {/* Куда легли деньги. У перевода счетов два, и второй бизнес
                  подписываем, только если он другой — иначе строка удваивалась бы */}
              <div className="mt-0.5 text-[12px]" style={{ color: "var(--tg-hint)" }}>
                {transfer
                  ? `${o.acc} → ${o.toBiz && o.toBiz !== o.biz ? `${o.toBiz} / ` : ""}${o.toAcc || "—"}`
                  : o.parts?.length > 1 ? partsLabel(o.parts)
                    : [o.biz, o.acc].filter(Boolean).join(" / ")}
              </div>
            </div>
          </li>
        );
      })}
    </List>
  );
}


/* ── Выгрузка отчётов ──
   Файл уходит документом в личку с ботом: скачанное внутри Telegram легко теряется,
   а в переписке отчёт остаётся и им можно поделиться. */
function Exports({ query }) {
  const [busy, setBusy] = useState(null);

  const send = (kind) => async () => {
    setBusy(kind);
    try {
      await api.post(`/exports/${kind}?${new URLSearchParams(
        Object.entries(query).filter(([, v]) => v != null && v !== "")
      )}`);
      haptic.success();
      alertDialog("Отчёт отправлен в чат с ботом.");
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(null);
    }
  };

  const Btn = ({ kind, label }) => (
    <button
      onClick={send(kind)}
      disabled={!!busy}
      className="card-tg press flex items-center justify-center gap-2 py-3 text-[15px] font-semibold disabled:opacity-50"
      style={{ color: "var(--tg-link)" }}
    >
      <Ion.cloudUpload size={18} />
      {busy === kind ? "Отправляем…" : label}
    </button>
  );

  return (
    <div className="px-4 pt-4 grid grid-cols-2 gap-2">
      <Btn kind="cashflow" label="ОДДС" />
      <Btn kind="pl" label="P&L" />
    </div>
  );
}
